export type VerificationMode = 'face' | 'pin' | 'card';

export interface StructureInfo {
  idStructure: number;
  code: string;
  path: string;
  structure: string;
  idTimezone: number;
  idStructureType: number;
  timezonePg: string;
}

export interface AccessRole {
  idAccessRole: number;
  name: string;
  typeLabel: string;
  employeeCount: number;
  isActive: boolean;
  verificationModes: VerificationMode[];
  idMarkingRole: number;
  structures: number[];
  structuresInfo: StructureInfo[];  // Structure details with timezone
}

import { saveMarkingTimezone } from '@/shared/utils/timezone';

const PROFILE_KEY = 'mi_profile_chosen';

export function saveSelectedProfile(role: AccessRole): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(role));
  
  // Save marking timezone from the first structure
  // This timezone will be used for all timelogs created under this profile
  if (role.structuresInfo.length > 0) {
    const firstStructure = role.structuresInfo[0];
    if (firstStructure) {
      saveMarkingTimezone(firstStructure.timezonePg).catch(err => {
        console.warn('[AccessRole] Failed to save marking timezone to Dexie:', err);
      });
    }
  }
}

export function loadSelectedProfile(): AccessRole | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AccessRole;
  } catch {
    return null;
  }
}

export function clearSelectedProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}

/**
 * Resolve a structure's human-readable code from an AccessRole's own
 * `structuresInfo` (profile/access-role scope), given a structure id already
 * selected for the profile (e.g. `role.structures[0]`).
 *
 * This is the correct scope for resolving `idStructure` set at timelog-draft
 * creation time (see PinPage/ScanningPage/CardScanPage), because that id
 * originates from the selected access role, NOT from the employee's own
 * `structuresInfo` (a different, unrelated array — see EmployeeDTO).
 *
 * Returns null when the structure isn't found in the role's own list.
 */
export function resolveStructureCode(role: AccessRole, idStructure: number): string | null {
  // Defensive: `structuresInfo` may be absent on stale cached profiles
  // (localStorage) written before this field existed, or in partial test
  // fixtures — never throw, just resolve to null.
  return role.structuresInfo?.find((s) => s.idStructure === idStructure)?.code ?? null;
}
